import { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { PackageCheck, AlertTriangle, RefreshCcw, Loader2, Bell, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import api from "../lib/api";
import ReceiveOrderDialog from "../components/inventory/ReceiveOrderDialog";
import OrderMessagesDialog from "../components/orders/OrderMessagesDialog";

const RECEIVABLE_STATUSES = new Set(["sent", "pending", "confirmed", "draft"]);

const fmtEUR = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "—";

/**
 * Página de Recepciones: pendientes y histórico con incidencias.
 */
export default function ReceptionsPage() {
  const { currentRestaurant } = useOutletContext();
  const [tab, setTab] = useState("pending");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiveOrder, setReceiveOrder] = useState(null);
  const [remindingId, setRemindingId] = useState(null);
  const [thread, setThread] = useState(null);
  const [ocrPrefill, setOcrPrefill] = useState(null);

  const load = useCallback(async () => {
    if (!currentRestaurant?.restaurant_id) return;
    setLoading(true);
    try {
      const data = await api.getOrders(currentRestaurant.restaurant_id);
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      toast.error("No se pudieron cargar las recepciones");
    } finally {
      setLoading(false);
    }
  }, [currentRestaurant?.restaurant_id]);

  useEffect(() => { load(); }, [load]);

  const handleRemind = async (orderId) => {
    if (!currentRestaurant?.restaurant_id) return;
    setRemindingId(orderId);
    try {
      const res = await api.request(
        `/restaurants/${currentRestaurant.restaurant_id}/orders/${orderId}/send-reminder`,
        { method: "POST" },
      );
      toast.success(`Recordatorio enviado a ${res?.recipient || "el proveedor"}`);
      await load();
    } catch (e) {
      toast.error(e?.message || "No se pudo enviar el recordatorio");
    } finally {
      setRemindingId(null);
    }
  };

  const { pending, history, claims } = useMemo(() => {
    const p = orders.filter((o) => RECEIVABLE_STATUSES.has(o.status) && o.source !== "auto_reorder");
    const h = orders
      .filter((o) => o.status === "received")
      .sort((a, b) => String(b.received_date || "").localeCompare(String(a.received_date || "")));
    const c = h.filter((o) =>
      Array.isArray(o.incidents) && o.incidents.length > 0
    );
    return { pending: p, history: h, claims: c };
  }, [orders]);

  const renderPending = () => {
    if (pending.length === 0) {
      return (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No hay pedidos pendientes de recibir. ¡Todo al día!
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="space-y-3">
        {pending.map((o) => (
          <Card key={o.order_id} data-testid={`reception-pending-${o.order_id}`}>
            <CardContent className="p-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-sm">{o.supplier_name || "Sin proveedor"}</p>
                  <Badge variant="outline" className="text-[10px]">
                    #{(o.order_id || "").slice(-8)}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {(o.items || []).length} ítems · {fmtEUR(o.total_amount)}
                  {o.sent_at && <span> · enviado {fmtDate(o.sent_at)}</span>}
                </p>
              </div>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => setReceiveOrder(o)}
                data-testid={`reception-btn-${o.order_id}`}
              >
                <PackageCheck className="w-3.5 h-3.5 mr-1.5" /> Recibir
              </Button>
            </CardContent>
            {(o.reminder_count || 0) > 0 && (
              <div className="px-4 pb-3 -mt-1">
                <p className="text-[11px] text-amber-700 dark:text-amber-300">
                  Recordatorio enviado {o.reminder_count}× · último{" "}
                  {o.last_reminder_at ? new Date(o.last_reminder_at).toLocaleDateString("es-ES") : "—"}
                </p>
              </div>
            )}
            <div className="px-4 pb-3 flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setThread(o)}
                data-testid={`reception-thread-${o.order_id}`}
              >
                <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                Mensajes{(o.messages_count || 0) > 0 ? ` (${o.messages_count})` : ""}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRemind(o.order_id)}
                disabled={remindingId === o.order_id}
                data-testid={`reception-remind-${o.order_id}`}
              >
                {remindingId === o.order_id ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Bell className="w-3.5 h-3.5 mr-1.5" />
                )}
                Enviar recordatorio al proveedor
              </Button>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  const renderHistoryTable = (rows) => {
    if (rows.length === 0) {
      return (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {tab === "claims" ? "No has registrado ninguna incidencia." : "Aún no hay recepciones registradas."}
          </CardContent>
        </Card>
      );
    }
    return (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proveedor</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-center">Items</TableHead>
                <TableHead className="text-center">Incidencias</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((o) => {
                const incCount = Array.isArray(o.incidents) ? o.incidents.length : 0;
                return (
                  <TableRow key={o.order_id} data-testid={`reception-row-${o.order_id}`}>
                    <TableCell>
                      <p className="font-medium text-sm">{o.supplier_name || "Sin proveedor"}</p>
                      <p className="text-xs text-muted-foreground">#{(o.order_id || "").slice(-8)}</p>
                    </TableCell>
                    <TableCell className="text-xs">{fmtDate(o.received_date)}</TableCell>
                    <TableCell className="text-right">{fmtEUR(o.total_amount)}</TableCell>
                    <TableCell className="text-center">{(o.items || []).length}</TableCell>
                    <TableCell className="text-center">
                      {incCount > 0 ? (
                        <Badge className="bg-amber-100 text-amber-800 text-[10px]">
                          <AlertTriangle className="w-3 h-3 mr-1" /> {incCount}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="receptions-page">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
            <PackageCheck className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Recepciones</h1>
            <p className="text-sm text-muted-foreground">
              Registra cantidades recibidas e incidencias para alimentar las puntuaciones de proveedores.
            </p>
          </div>
        </div>
        <Button
          variant="outline" size="sm" onClick={load} disabled={loading}
          data-testid="receptions-refresh-btn"
        >
          <RefreshCcw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      {loading ? (
        <Card><CardContent className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></CardContent></Card>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="receptions-tab-pending">
              Pendientes
              {pending.length > 0 && <Badge variant="secondary" className="ml-2">{pending.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="receptions-tab-history">Histórico</TabsTrigger>
            <TabsTrigger value="claims" data-testid="receptions-tab-claims">
              Incidencias
              {claims.length > 0 && <Badge variant="secondary" className="ml-2">{claims.length}</Badge>}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="pending" className="mt-5">{renderPending()}</TabsContent>
          <TabsContent value="history" className="mt-5">{renderHistoryTable(history)}</TabsContent>
          <TabsContent value="claims" className="mt-5">{renderHistoryTable(claims)}</TabsContent>
        </Tabs>
      )}

      <ReceiveOrderDialog
        open={!!receiveOrder}
        onOpenChange={(o) => { if (!o) { setReceiveOrder(null); setOcrPrefill(null); } }}
        order={receiveOrder}
        prefillFromOcr={ocrPrefill}
        onSuccess={() => { setReceiveOrder(null); setOcrPrefill(null); load(); }}
      />
      <OrderMessagesDialog
        open={!!thread}
        onOpenChange={(o) => { if (!o) setThread(null); }}
        restaurantId={currentRestaurant?.restaurant_id}
        order={thread}
        onPrefilledReceive={(detectedItems) => {
          // Open ReceiveOrderDialog with detected items pre-filled
          setOcrPrefill(detectedItems);
          setReceiveOrder(thread);
        }}
      />
    </div>
  );
}
